// The assistant's site search cards (plan 2026-10-28-site-search, step 5.5).
//
// On confirm the user's browser calls the same settings route the site
// search settings page calls, under the user's own JWT: the route decides
// (account admin, the flag, this account's site) and validates again. The
// card's params are the values the proposal tool normalised, so nothing is
// re-derived here. `open_site_search_settings` only navigates: the change is
// made on the page.
import { ApiError, apiJson } from "@/api/client";
import { ActionError, registerAction } from "./registry";

function sitePath(siteId: string, tail: string): string {
  return `/site-search/sites/${encodeURIComponent(siteId)}/${tail}`;
}

function settingsLink(siteId: string, section: string) {
  return {
    label: "Open site search settings",
    to: `/settings/site-search/${encodeURIComponent(siteId)}?section=${section}`,
  };
}

/** A refusal the route words for the user (a duplicate, a stale proposal) reaches them intact. */
async function call<T>(url: string, body?: Record<string, unknown>): Promise<T> {
  try {
    return await apiJson<T>(url, {
      method: "POST",
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 409 || err.status === 422)) {
      throw new ActionError(err.message);
    }
    throw err;
  }
}

registerAction("add_site_search_synonym", async (params) => {
  const siteId = params.site_id as string;
  const synonym = await call<{ id: string }>(sitePath(siteId, "synonyms"), {
    term: params.term, synonym: params.synonym, direction: params.direction,
  });
  return { resultRef: { site_id: siteId, synonym_id: synonym.id }, link: settingsLink(siteId, "synonyms") };
});

registerAction("add_site_search_rule", async (params) => {
  const siteId = params.site_id as string;
  const rule = await call<{ id: string }>(sitePath(siteId, "rules"), {
    query: params.query, match_type: params.match_type, action: params.action,
    target: params.target, position: params.position ?? null,
  });
  return { resultRef: { site_id: siteId, rule_id: rule.id }, link: settingsLink(siteId, "rules") };
});

registerAction("decide_site_search_proposal", async (params) => {
  const siteId = params.site_id as string;
  const proposalId = params.proposal_id as string;
  const decision = params.decision === "accept" ? "accept" : "reject";
  await call(sitePath(siteId, `synonym-proposals/${encodeURIComponent(proposalId)}/${decision}`));
  return {
    resultRef: { site_id: siteId, proposal_id: proposalId, decision },
    link: settingsLink(siteId, decision === "accept" ? "synonyms" : "proposals"),
  };
});

registerAction("open_site_search_settings", async (params) => {
  const to = params.path as string;
  // Only the settings page: the path comes from the proposal tool, but a
  // card must never navigate anywhere else.
  if (typeof to !== "string" || !to.startsWith("/settings/site-search")) {
    throw new ActionError("This card does not open a site search page.");
  }
  return { resultRef: { path: to }, link: { label: "Open site search settings", to }, navigateTo: to };
});
